import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Download, 
  RefreshCw, 
  Clock, 
  HardDrive, 
  AlertCircle, 
  CheckCircle, 
  Calendar,
  Database
} from 'lucide-react';
import api from '../utils/api';
import RestoreModal from './RestoreModal';

const BackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [offsite, setOffsite] = useState([]);
  const [offsiteLoading, setOffsiteLoading] = useState(false);
  const [offsiteDownloading, setOffsiteDownloading] = useState(null);
  const [driveFiles, setDriveFiles] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [driveLoading, setDriveLoading] = useState(false);
  const [dedupLoading, setDedupLoading] = useState(false);

  const handleCleanupDuplicates = async () => {
    setDedupLoading(true);
    try {
      const check = await api.post('/backup/drive/cleanup-duplicates?dry_run=true');
      if (check.data.error) { alert('Errore: ' + check.data.error); return; }
      if (!check.data.duplicates) { alert('Nessun doppione trovato su Drive ✅'); return; }
      if (!window.confirm(`Trovati ${check.data.duplicates} doppioni (${check.data.freed_mb} MB recuperabili).\nEliminare le copie duplicate? (viene tenuta 1 copia per ogni giornata)`)) return;
      const r = await api.post('/backup/drive/cleanup-duplicates?dry_run=false');
      if (r.data.error) { alert('Errore: ' + r.data.error); return; }
      alert(`✅ Eliminati ${r.data.deleted} doppioni — liberati ${r.data.freed_mb} MB su Drive`);
      fetchDriveList();
    } catch (e) {
      alert('Errore pulizia doppioni: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDedupLoading(false);
    }
  };

  const fetchDriveList = async () => {
    setDriveLoading(true);
    try {
      const r = await api.get('/backup/drive/list');
      setDriveFiles(r.data);
    } catch (e) {
      alert('Errore elenco Drive: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDriveLoading(false);
    }
  };

  const fetchOffsite = async () => {
    setOffsiteLoading(true);
    try {
      const r = await api.get('/backup/offsite');
      const objs = (r.data.objects || []).sort((a, b) => String(a.path).localeCompare(String(b.path)));
      setOffsite(objs);
    } catch (e) {
      alert('Errore caricamento elenco off-site: ' + (e.response?.data?.detail || e.message));
    } finally {
      setOffsiteLoading(false);
    }
  };

  const handleOffsiteDownload = async (path) => {
    setOffsiteDownloading(path);
    try {
      const response = await api.get(`/backup/offsite/download?path=${encodeURIComponent(path)}`, {
        responseType: 'blob', timeout: 900000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = path.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Errore download: ' + (e.response?.data?.detail || e.message));
    } finally {
      setOffsiteDownloading(null);
    }
  };

  useEffect(() => {
    fetchBackupData();
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected')) {
      alert('✅ Google Drive collegato! Da ora ogni backup verrà copiato anche nella cartella "HWsmart-Backups" del tuo Drive.');
      window.history.replaceState({}, '', '/backup');
    } else if (params.get('drive_error')) {
      alert(`❌ Collegamento Google Drive non riuscito: ${params.get('drive_error')}`);
      window.history.replaceState({}, '', '/backup');
    }
  }, []);

  const handleConnectDrive = async () => {
    try {
      const r = await api.get('/backup/drive/connect');
      window.location.href = r.data.authorization_url;
    } catch (e) {
      alert('❌ Impossibile avviare il collegamento: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm('Scollegare Google Drive? I backup non verranno più copiati sul tuo Drive (quelli già caricati restano).')) return;
    try {
      await api.delete('/backup/drive');
      await fetchBackupData();
    } catch (e) {
      alert('❌ Errore durante lo scollegamento');
    }
  };

  const fetchBackupData = async () => {
    try {
      setLoading(true);
      const [backupsRes, statusRes] = await Promise.all([
        api.get('/backup/list'),
        api.get('/backup/status')
      ]);
      setBackups(backupsRes.data.backups || []);
      setStatus(statusRes.data);
      if (statusRes.data.running && !backupInProgress) pollUntilDone();
    } catch (error) {
      console.error('Error fetching backup data:', error);
      alert('Errore nel caricamento dei backup');
    } finally {
      setLoading(false);
    }
  };

  const pollUntilDone = async () => {
    setBackupInProgress(true);
    const start = Date.now();
    try {
      while (Date.now() - start < 20 * 60 * 1000) {
        await new Promise(res => setTimeout(res, 3000));
        const s = await api.get('/backup/status');
        setStatus(s.data);
        if (!s.data.running) {
          const lr = s.data.last_result;
          if (lr?.success) {
            alert(`✅ Backup completato!\nFile: ${lr.filename}\nDimensione: ${(lr.size / 1048576).toFixed(1)} MB\nCopia off-site: ${lr.cloud_backup ? 'OK' : 'NON riuscita (solo locale)'}\nGoogle Drive: ${lr.file_id ? 'OK ✅' : 'non caricato'}`);
          } else if (lr) {
            alert(`❌ Backup fallito: ${lr.error || 'errore sconosciuto'}`);
          }
          await fetchBackupData();
          return;
        }
      }
      alert('⏳ Il backup sta impiegando più del previsto: controlla lo stato tra qualche minuto con "Aggiorna".');
    } finally {
      setBackupInProgress(false);
    }
  };

  const handleManualBackup = async () => {
    if (backupInProgress) return;

    const confirmBackup = window.confirm('Vuoi creare un backup manuale del database?\n\nIl backup parte in background e può durare qualche minuto: la pagina si aggiornerà da sola al termine.');
    if (!confirmBackup) return;

    try {
      setBackupInProgress(true);
      const r = await api.post('/backup/manual');
      if (r.data.already_running) {
        alert('⏳ Un backup è già in corso: attendi il completamento.');
      }
      await pollUntilDone();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('❌ Errore durante l\'avvio del backup');
      setBackupInProgress(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await api.get(`/backup/download/${filename}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading backup:', error);
      alert('Errore durante il download del backup');
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Layout title="Gestione Backup">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestione Backup">
      <div className="space-y-6 pb-24">
        {/* Status Card */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Database className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Sistema di Backup</h2>
                <p className="text-sm text-gray-600">Gestione backup automatici del database</p>
              </div>
            </div>
            <Button
              data-testid="backup-manual-btn"
              onClick={handleManualBackup}
              disabled={backupInProgress}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {backupInProgress ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Backup in corso...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Backup Manuale
                </>
              )}
            </Button>
          </div>

          {/* Status Info */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 text-green-600 mb-2">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">Stato</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">Attivo</p>
              <p className="text-xs text-gray-500 mt-1">Sistema operativo</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 text-blue-600 mb-2">
                <Clock className="h-5 w-5" />
                <span className="font-semibold">Schedulazione</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{status?.schedule || 'Daily at 3:00'}</p>
              <p className="text-xs text-gray-500 mt-1">Automatico</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 text-purple-600 mb-2">
                <HardDrive className="h-5 w-5" />
                <span className="font-semibold">Backup Disponibili</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{status?.total_backups || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{status?.retention_days} giorni retention</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 text-orange-600 mb-2">
                <Calendar className="h-5 w-5" />
                <span className="font-semibold">Ultimo Backup</span>
              </div>
              <p className="text-sm font-bold text-gray-900">
                {status?.latest_backup ? formatDate(status.latest_backup.created) : 'Nessuno'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {status?.latest_backup ? formatSize(status.latest_backup.size) : '-'}
              </p>
            </div>
          </div>
        </Card>

        {/* Google Drive */}
        <Card className="p-4 border border-gray-200" data-testid="drive-connect-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${status?.google_drive?.connected ? 'bg-green-100' : 'bg-gray-100'}`}>
                <HardDrive className={`h-5 w-5 ${status?.google_drive?.connected ? 'text-green-600' : 'text-gray-500'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Copia su Google Drive</h3>
                {status?.google_drive?.connected ? (
                  <p className="text-sm text-green-700" data-testid="drive-status-connected">
                    ✅ Collegato a <b>{status.google_drive.account_email || 'account Google'}</b> — ogni backup viene copiato nella cartella "HWsmart-Backups" (ultimi 15 giornalieri + 4 pesanti conservati)
                  </p>
                ) : (
                  <p className="text-sm text-gray-500" data-testid="drive-status-disconnected">
                    Non collegato — collega il tuo Google Drive per avere una copia dei backup direttamente sul tuo account
                  </p>
                )}
              </div>
            </div>
            {status?.google_drive?.connected ? (
              <Button variant="outline" size="sm" data-testid="drive-disconnect-btn" onClick={handleDisconnectDrive}>
                Scollega
              </Button>
            ) : (
              <Button data-testid="drive-connect-btn" onClick={handleConnectDrive} className="bg-green-600 hover:bg-green-700 text-white">
                Collega Google Drive
              </Button>
            )}
          </div>
        </Card>

        {/* Info Alert */}
        <Card className="p-4 bg-blue-50 border border-blue-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">Informazioni Backup</h3>
              <p className="text-sm text-blue-700 mt-1">
                • I backup vengono creati automaticamente ogni notte alle {status?.schedule?.split('at ')[1] || '3:00'}<br/>
                • I backup più vecchi di {status?.retention_days || 30} giorni vengono eliminati automaticamente<br/>
                • Puoi scaricare i backup e salvarli su Google Drive, Dropbox o disco esterno<br/>
                • File formato: JSON compressi (gzip) - decomprimi con WinRAR, 7-Zip o simili
              </p>
            </div>
          </div>
        </Card>

        {/* Backups List */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">📦 Backup Disponibili</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBackupData}
              className="border-gray-300"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
          </div>

          {status?.running && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="backup-progress-row">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 font-semibold text-blue-900 text-sm">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Backup in corso...
                </span>
                <span className="text-sm font-bold text-blue-700" data-testid="backup-progress-percent">
                  {status?.progress?.percent ?? 0}%
                </span>
              </div>
              <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-700"
                  style={{ width: `${status?.progress?.percent ?? 0}%` }}
                />
              </div>
              <p className="text-xs text-blue-600 mt-2" data-testid="backup-progress-detail">
                {status?.progress?.detail || 'Preparazione...'}
                {status?.running_since ? ` — avviato alle ${new Date(status.running_since).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}` : ''}
              </p>
            </div>
          )}

          {backups.length === 0 ? (
            <div className="text-center py-12">
              <HardDrive className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nessun backup disponibile</p>
              <p className="text-sm text-gray-400 mt-2">
                Crea il primo backup usando il pulsante "Backup Manuale"
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-semibold text-gray-700">Data e Ora</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Tipo</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Nome File</th>
                    <th className="text-right p-3 font-semibold text-gray-700">Dimensione</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup, index) => (
                    <tr 
                      key={backup.filename} 
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-blue-50' : ''}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {formatDate(backup.created)}
                          </span>
                          {index === 0 && (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              Più recente
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={backup.type === 'manual' 
                          ? "bg-blue-100 text-blue-800" 
                          : "bg-gray-100 text-gray-800"}>
                          {backup.type === 'manual' ? '👤 Manuale' : '🤖 Automatico'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-gray-600 font-mono">
                          {backup.filename}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-sm font-medium text-gray-900">
                          {formatSize(backup.size)}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleDownload(backup.filename)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Scarica
                          </Button>
                          <Button size="sm" variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => setRestoreTarget({ source: 'local', name: backup.filename })}
                            data-testid={`restore-local-${backup.filename}`}>
                            Ripristina
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Backup su Google Drive */}
        <Card className="p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">🟢 Backup su Google Drive</h3>
              <p className="text-sm text-gray-500 mt-1">File presenti nella cartella "HWsmart-Backups" del Drive collegato (ultimi 15 giornalieri + 4 settimanali pesanti).</p>
            </div>
            <div className="flex gap-2">
              {driveFiles?.connected && (
                <Button variant="outline" size="sm" onClick={handleCleanupDuplicates} disabled={dedupLoading}
                  className="text-red-600 border-red-200 hover:bg-red-50" data-testid="drive-dedup-btn">
                  {dedupLoading ? 'Controllo…' : 'Elimina doppioni'}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={fetchDriveList} disabled={driveLoading} data-testid="drive-list-refresh-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${driveLoading ? 'animate-spin' : ''}`} />
                {driveFiles ? 'Aggiorna' : 'Carica elenco'}
              </Button>
            </div>
          </div>
          {!driveFiles ? (
            <p className="text-sm text-gray-400">{driveLoading ? 'Caricamento…' : 'Premi "Carica elenco" per verificare cosa c\'è davvero su Drive.'}</p>
          ) : !driveFiles.connected ? (
            <p className="text-sm text-amber-600">Google Drive non collegato: usa "Collega Drive" qui sopra.</p>
          ) : driveFiles.files.length === 0 ? (
            <p className="text-sm text-red-600">⚠️ Nessun backup trovato nella cartella "{driveFiles.folder}" — le copie su Drive non stanno funzionando, segnalalo.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-semibold text-gray-700">Data</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Nome file</th>
                    <th className="text-right p-3 font-semibold text-gray-700">Dimensione</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {driveFiles.files.map((f) => (
                    <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-700">{new Date(f.createdTime).toLocaleString('it-IT')}</td>
                      <td className="p-3 text-sm font-mono text-gray-700">
                        {f.name}
                        {f.name.includes('_heavy_') && <Badge className="bg-purple-100 text-purple-800 text-xs ml-2">pesante</Badge>}
                      </td>
                      <td className="p-3 text-right text-sm font-medium text-gray-900">{f.size ? formatSize(Number(f.size)) : '—'}</td>
                      <td className="p-3 text-center">
                        {f.webViewLink && (
                          <a href={f.webViewLink} target="_blank" rel="noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-testid={`drive-open-${f.id}`}>
                            Apri su Drive
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Off-site (Object Storage) */}
        <Card className="p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">☁️ Copie off-site (Object Storage)</h3>
              <p className="text-sm text-gray-500 mt-1">Scarica qui una copia di sicurezza prima di far ripulire i vecchi file dal supporto Emergent.</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchOffsite} disabled={offsiteLoading} data-testid="offsite-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${offsiteLoading ? 'animate-spin' : ''}`} />
              {offsite.length ? 'Aggiorna' : 'Carica elenco'}
            </Button>
          </div>
          {offsite.length === 0 ? (
            <p className="text-sm text-gray-400">{offsiteLoading ? 'Caricamento…' : 'Premi "Carica elenco" per vedere le copie salvate nello storage.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-semibold text-gray-700">File</th>
                    <th className="text-right p-3 font-semibold text-gray-700">Dimensione</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {offsite.map((o) => (
                    <tr key={o.path} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <span className="text-sm text-gray-700 font-mono">{o.path}</span>
                        {String(o.path || '').includes('db_backups_v2') && (
                          <Badge className="bg-green-100 text-green-800 text-xs ml-2">nuovo formato</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right text-sm font-medium text-gray-900">{o.size ? formatSize(o.size) : '—'}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button size="sm" variant="outline" disabled={offsiteDownloading === o.path}
                            onClick={() => handleOffsiteDownload(o.path)}
                            data-testid={`offsite-download-${o.path}`}>
                            <Download className="h-4 w-4 mr-1" />
                            {offsiteDownloading === o.path ? 'Download…' : 'Scarica'}
                          </Button>
                          {String(o.path || '').endsWith('.json.gz') && (
                            <Button size="sm" variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => setRestoreTarget({ source: 'offsite', name: o.path })}
                              data-testid={`restore-offsite-${o.path}`}>
                              Ripristina
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {restoreTarget && <RestoreModal target={restoreTarget} onClose={() => setRestoreTarget(null)} />}
      </div>
    </Layout>
  );
};

export default BackupManager;
